import type { Narrative } from './types';

export const narratives: Narrative[] = [
  {
    id: 'from-fleece-to-coat',
    title: 'Od runa do płaszcza',
    eyebrow: 'ŚCIEŻKA MATERIAŁU / 01',
    description: 'Włókno nie przechodzi przez jedną maszynę. Zmienia się przez wodę, nacisk, skręt, splot i powierzchnię.',
    estimatedMinutes: 8,
    epilogue: 'Materiał opuszcza halę. Jego napięcie zostaje w maszynach, budynkach i pamięci miasta.',
    atlasYear: 2026,
    atlasSiteId: 'zpw-03-1-maja-8',
    connections: [{ storyId: 'one-factory-thousand-relations', reason: 'Dalej przez miejsce · Stara Fabryka', bridgeNodeId: 'arch-old-factory-museum' }],
    steps: [
      {
        id: 'fleece-01',
        title: 'Najpierw jest odległość',
        lead: 'Bielska tkanina zaczyna się poza Bielskiem — w sieci surowców, dostaw i granic.',
        nodeIds: ['trade-raw-wool-import', 'place-bielsko', 'textile-bielska-wool'],
        relationIds: ['r-import-cloth', 'r-import-bielsko'],
        layoutMode: 'network',
        cameraTarget: { nodeId: 'trade-raw-wool-import', distance: 8 },
        duration: 4800,
        transition: 'ease',
        presenterNotes: 'Zacznij od pytania: czy lokalny produkt ma lokalny początek?',
      },
      {
        id: 'fleece-02',
        title: 'Woda otwiera włókno',
        lead: 'Pranie wełny wymaga infrastruktury. Rzeka i wodociąg są częścią procesu, nawet jeśli nie widać ich w hali.',
        nodeIds: ['process-wool-washing', 'tech-industrial-water', 'map-industrial-water', 'place-wapienica'],
        relationIds: ['r-washing-water', 'r-water-plan-tech', 'r-water-map'],
        layoutMode: 'map',
        cameraTarget: { nodeId: 'tech-industrial-water', distance: 7 },
        duration: 5600,
        transition: 'draw',
        presenterNotes: 'Pozwól mapie zostać chwilę na ekranie; to pierwszy kontekst poza fabryką.',
      },
      {
        id: 'fleece-03',
        title: 'Rozluźnienie nie oznacza chaosu',
        lead: 'Wilk rozdziela i miesza surowiec, aby można było zbudować nową, bardziej równą strukturę.',
        nodeIds: ['machine-wilk', 'process-opening-mixing', 'tech-wool-blending'],
        relationIds: ['r-wilk-opening', 'r-opening-tech'],
        layoutMode: 'process',
        cameraTarget: { nodeId: 'machine-wilk', distance: 6 },
        duration: 5000,
        transition: 'settle',
        presenterNotes: 'Aktywuj lokalne napięcie i pokaż, że odkształcenie dotyczy tylko sąsiedztwa.',
      },
      {
        id: 'fleece-04',
        title: 'Włókno dostaje kierunek',
        lead: 'Zgrzeblenie porządkuje wiele luźnych końców. Przędzenie nadaje im skręt i ciągłość.',
        nodeIds: ['process-carding', 'machine-conical-carding', 'process-spinning', 'machine-selfactor'],
        relationIds: ['r-carding-machine', 'r-carding-spinning', 'r-selfactor-spinning'],
        layoutMode: 'process',
        cameraTarget: { nodeId: 'machine-selfactor', distance: 6.5 },
        duration: 6200,
        transition: 'ease',
        presenterNotes: 'Podkreśl zmianę geometrii: z chmury włókien powstaje linia nitki.',
      },
      {
        id: 'fleece-05',
        title: 'Splot jest relacją',
        lead: 'Tkanie nie tworzy tkaniny z jednej nici. Powstaje ona z przecięcia kierunków.',
        nodeIds: ['process-spinning', 'process-weaving', 'tech-mechanized-weaving', 'textile-wool-fabric'],
        relationIds: ['r-spinning-weaving', 'r-weaving-technology', 'r-weaving-fabric'],
        layoutMode: 'network',
        cameraTarget: { nodeId: 'process-weaving', distance: 6 },
        duration: 5600,
        transition: 'draw',
        presenterNotes: 'To centralna metafora projektu — nie pokazuj całego grafu, tylko aktywny splot.',
      },
      {
        id: 'fleece-06',
        title: 'Powierzchnia ma pamięć nacisku',
        lead: 'Folowanie, drapanie i postrzyganie nadają sukniowi gęstość, włos i dotyk.',
        nodeIds: ['textile-cloth', 'process-fulling', 'machine-fulling', 'process-napping-shearing', 'machine-shearing'],
        relationIds: ['r-cloth-fulling', 'r-fulling-machine', 'r-fulling-surface', 'r-shearing-machine'],
        layoutMode: 'process',
        cameraTarget: { nodeId: 'textile-cloth', distance: 6 },
        duration: 6200,
        transition: 'settle',
        presenterNotes: 'Zostaw napis krótki; rytm powinien budować obraz powierzchni.',
      },
      {
        id: 'fleece-07',
        title: 'Ta sama wiedza, inna forma',
        lead: 'Włókno może stać się także filcem i kapeluszem — materiał rozgałęzia się w produkt.',
        nodeIds: ['textile-felt-hat', 'machine-felting', 'machine-hat-block', 'machine-stretching'],
        relationIds: ['r-hat-felting', 'r-hat-block', 'r-hat-stretch'],
        layoutMode: 'cluster',
        cameraTarget: { nodeId: 'textile-felt-hat', distance: 6.5 },
        duration: 5200,
        transition: 'ease',
        presenterNotes: 'Pokaż boczną gałąź, żeby narracja nie udawała jednej liniowej fabryki.',
      },
      {
        id: 'fleece-08',
        title: 'Materiał zostaje w mieście',
        lead: 'Płaszcz znika z hali, ale relacje zostają w maszynach, budynkach i pamięci.',
        nodeIds: ['textile-cloth', 'heritage-museum-technology-1979', 'arch-old-factory-museum', 'textile-bielska-wool'],
        relationIds: ['r-old-factory-heritage', 'r-museum-founded-event', 'r-museum-workers'],
        layoutMode: 'network',
        cameraTarget: { nodeId: 'arch-old-factory-museum', distance: 8 },
        duration: 6200,
        transition: 'settle',
        presenterNotes: 'Zakończ powrotem do szerszego grafu i zaproponuj przejście do samodzielnego badania.',
      },
    ],
  },
  {
    id: 'one-factory-thousand-relations',
    title: 'Jedna fabryka, tysiąc relacji',
    eyebrow: 'ŚCIEŻKA MIEJSCA / 02',
    description: 'Historia jednego adresu nie mieści się w nazwie zakładu. Zmieniają się właściciele, maszyny, funkcje i sposoby pamiętania.',
    estimatedMinutes: 7,
    epilogue: 'Jedna hala przetrwała wiele nazw. Teraz zobacz, jak podobna zmiana objęła całe miasto.',
    atlasYear: 1948,
    atlasSiteId: 'zpw-03-1-maja-8',
    connections: [{ storyId: 'private-to-zpw', reason: 'Dalej przez czas · rok 1948', bridgeNodeId: 'event-state-takeover-1945' }],
    steps: [
      {
        id: 'factory-01',
        title: 'Adres przed nazwą',
        lead: 'Żywieckie Przedmieście było miejscem produkcji, zanim stało się miejscem muzealnym.',
        nodeIds: ['place-zywieckie-przedmiescie', 'arch-buttner-hall', 'arch-old-factory-museum'],
        relationIds: ['r-zywieckie-old', 'r-buttner-hall'],
        layoutMode: 'cluster',
        cameraTarget: { nodeId: 'place-zywieckie-przedmiescie', distance: 8 },
        duration: 5200,
        transition: 'ease',
        presenterNotes: 'Najpierw pokaż miejsce, dopiero potem nazwisko Büttnera.',
      },
      {
        id: 'factory-02',
        title: 'Rodzina jako infrastruktura',
        lead: 'Karol, Karol Teodor i Gustav Adolf: przedsiębiorstwo trwa przez sukcesję.',
        nodeIds: ['person-karol-buttner', 'person-karol-teodor-buttner', 'person-gustav-adolf-buttner', 'company-buttner'],
        relationIds: ['r-buttner-founder', 'r-buttner-karol-teodor', 'r-buttner-gustav', 'r-karol-teodor-lineage', 'r-gustav-lineage'],
        layoutMode: 'network',
        cameraTarget: { nodeId: 'company-buttner', distance: 7 },
        duration: 5600,
        transition: 'draw',
        presenterNotes: 'Pozwól, aby relacje rodzinne pojawiały się kolejno, jak kolejne warstwy hali.',
      },
      {
        id: 'factory-03',
        title: 'Zakład jako ciąg czynności',
        lead: 'Pralnia, przędzalnia, tkalnia, drapalnia, farbiarnia i wykończalnia — jedna nazwa kryje cały proces.',
        nodeIds: ['company-buttner', 'process-wool-washing', 'process-spinning', 'process-weaving', 'process-napping-shearing', 'textile-cloth'],
        relationIds: ['r-buttner-washing', 'r-buttner-spinning', 'r-buttner-weaving', 'r-buttner-finishing', 'r-buttner-cloth'],
        layoutMode: 'process',
        cameraTarget: { nodeId: 'company-buttner', distance: 7 },
        duration: 6200,
        transition: 'draw',
        presenterNotes: 'Nie próbuj opowiedzieć wszystkich etapów naraz; odsłoń je jako splot.',
      },
      {
        id: 'factory-04',
        title: 'Maszyna po zmianie właściciela',
        lead: 'Po 1945 roku zmienia się nie tylko szyld. Zmienia się system, w którym maszyna pracuje.',
        nodeIds: ['event-state-takeover-1945', 'doc-nationalization-1945', 'company-bewelana', 'heritage-museum-technology-1979'],
        relationIds: ['r-state-document', 'r-state-buttner', 'r-buttner-successions'],
        layoutMode: 'timeline',
        cameraTarget: { nodeId: 'event-state-takeover-1945', distance: 7 },
        duration: 6000,
        transition: 'settle',
        presenterNotes: 'To moment historyczny, nie ocena; pokaż zmianę relacji i nazw.',
      },
      {
        id: 'factory-05',
        title: 'Po produkcji zostaje kolekcja',
        lead: 'W 1979 roku maszyny zaczynają być gromadzone jako świadectwa przemysłu.',
        nodeIds: ['event-museum-founded-1979', 'heritage-museum-technology-1979', 'machine-wilk', 'machine-selfactor', 'machine-shearing'],
        relationIds: ['r-museum-founded-event', 'r-machines-heritage', 'r-fulling-heritage'],
        layoutMode: 'cluster',
        cameraTarget: { nodeId: 'heritage-museum-technology-1979', distance: 7 },
        duration: 5600,
        transition: 'ease',
        presenterNotes: 'Wprowadź fotografię maszyn jako materiał, nie ozdobę.',
      },
      {
        id: 'factory-06',
        title: 'Jedna fabryka nie jest samotna',
        lead: 'Büttner łączy się z Wapienicą, BEFAMĄ, rynkiem surowca i całym miastem.',
        nodeIds: ['company-buttner', 'company-mehlo-kramer', 'company-befama', 'trade-raw-wool-import', 'place-bielsko-biala'],
        relationIds: ['r-mehlo-state', 'r-josephy-befama', 'r-buttner-import', 'r-city-museum'],
        layoutMode: 'network',
        cameraTarget: { nodeId: 'company-buttner', distance: 9 },
        duration: 6200,
        transition: 'settle',
        presenterNotes: 'To przejście do pełnego badania grafu — fabryka ma tysiąc relacji, bo miasto je podtrzymuje.',
      },
    ],
  },
  {
    id: 'city-woven-from-industry',
    title: 'Miasto utkane z przemysłu',
    eyebrow: 'ŚCIEŻKA MIASTA / 03',
    description: 'Bielsko i Biała tworzą miejski splot fabryk, rzek, placów, migracji kapitału i późniejszych przekształceń.',
    estimatedMinutes: 8,
    epilogue: 'Fabryki nie znikają jednocześnie. Jedne zostają muzeum, inne adresem, a jeszcze inne luką w mieście.',
    atlasYear: 2026,
    atlasSiteId: 'zpw-12-plac-fabryczny',
    connections: [{ storyId: 'private-to-zpw', reason: 'Dalej przez system · od fabryk do ZPW', bridgeNodeId: 'event-state-takeover-1945' }],
    steps: [
      {
        id: 'city-01',
        title: 'Dwa miasta, jeden splot',
        lead: 'Zanim powstała wspólna nazwa, Bielsko i Biała miały własne brzegi, adresy i historie zakładów.',
        nodeIds: ['place-bielsko', 'place-biala', 'place-bielsko-biala', 'event-industrialization'],
        relationIds: ['r-bielsko-biala-merge', 'r-biala-merge', 'r-bielsko-industrialization', 'r-biala-industrialization'],
        layoutMode: 'network',
        cameraTarget: { nodeId: 'place-bielsko-biala', distance: 9 },
        duration: 5600,
        transition: 'draw',
        presenterNotes: 'Zachowaj rozdzielenie dwóch nazw, zanim pojawi się wspólna.',
      },
      {
        id: 'city-02',
        title: 'Fabryka ma adres',
        lead: 'Sukiennicza, plac Fabryczny, Powstańców Śląskich, Kamienica i Wapienica tworzą mapę pracy.',
        nodeIds: ['place-fabryczny-square', 'place-kamienica', 'place-wapienica', 'arch-wega-quarter', 'arch-befama-complex', 'company-stosius', 'company-mehlo-kramer'],
        relationIds: ['r-fabryczny-wega', 'r-stosius-kamienica', 'r-mehlo-wapienica', 'r-befama-complex'],
        layoutMode: 'map',
        cameraTarget: { nodeId: 'place-fabryczny-square', distance: 10 },
        duration: 6200,
        transition: 'ease',
        presenterNotes: 'Czytaj adresy jak nici łączące oddalone fragmenty miasta.',
      },
      {
        id: 'city-03',
        title: 'Przemysł potrzebuje infrastruktury',
        lead: 'Woda i energia parowa nie są tłem. One wyznaczają, gdzie i jak może pracować fabryka.',
        nodeIds: ['tech-industrial-water', 'map-industrial-water', 'tech-steam-drive', 'arch-buttner-hall'],
        relationIds: ['r-water-plan-tech', 'r-water-map', 'r-steam-process', 'r-steam-weaving'],
        layoutMode: 'map',
        cameraTarget: { nodeId: 'tech-industrial-water', distance: 8 },
        duration: 5800,
        transition: 'draw',
        presenterNotes: 'Pokaż plan jako źródło techniczne, nie jako dekoracyjną mapę.',
      },
      {
        id: 'city-04',
        title: 'Miasto patrzy na ludzi',
        lead: 'Fotografia fabryki Vogta przywraca zbiorowy wymiar pracy, często ukryty za nazwą przedsiębiorstwa.',
        nodeIds: ['photo-vogt-1907', 'company-vogt', 'place-biala', 'heritage-city-hundred-industries'],
        relationIds: ['r-vogt-photo', 'r-vogt-biala', 'r-vogt-workers'],
        layoutMode: 'cluster',
        cameraTarget: { nodeId: 'photo-vogt-1907', distance: 7 },
        duration: 5600,
        transition: 'ease',
        presenterNotes: 'Zatrzymaj się przy obrazie dłużej niż przy opisie.',
      },
      {
        id: 'city-05',
        title: 'Pożar rozrywa splot',
        lead: 'Pożary BEFAMY i drugiej fabryki Büttnerów pokazują, że ciągłość przemysłu nie była dana.',
        nodeIds: ['event-befama-fire-1926', 'event-buttner-fire-1935', 'company-josephy', 'company-buttner', 'place-biala'],
        relationIds: ['r-befama-fire', 'r-buttner-fire-biala', 'r-buttner-fire-company'],
        layoutMode: 'timeline',
        cameraTarget: { nodeId: 'event-befama-fire-1926', distance: 8 },
        duration: 5600,
        transition: 'settle',
        presenterNotes: 'Nie animuj ognia; pokaż przerwę w strukturze i późniejsze konsekwencje.',
      },
      {
        id: 'city-06',
        title: 'Zmiana skali i własności',
        lead: 'Po 1945 roku wiele prywatnych zakładów trafia do jednego państwowego porządku.',
        nodeIds: ['doc-nationalization-1945', 'event-state-takeover-1945', 'company-mehlo-kramer', 'company-breitbart', 'company-bewelana'],
        relationIds: ['r-state-document', 'r-state-mehlo', 'r-state-breitbart', 'r-state-buttner'],
        layoutMode: 'timeline',
        cameraTarget: { nodeId: 'doc-nationalization-1945', distance: 8 },
        duration: 6200,
        transition: 'draw',
        presenterNotes: 'Zaznacz, że zmienia się organizacja, nie znika materiał ani praca.',
      },
      {
        id: 'city-07',
        title: 'Fabryka znika, relacja zostaje',
        lead: 'Sfera, Wega i Stara Fabryka pokazują trzy różne przyszłości przemysłowego miejsca.',
        nodeIds: ['photo-landesmann', 'photo-wega', 'arch-old-factory-museum', 'heritage-city-hundred-industries', 'heritage-museum-technology-1979'],
        relationIds: ['r-landesmann-transform', 'r-wega-heritage', 'r-city-museum', 'r-old-factory-heritage'],
        layoutMode: 'network',
        cameraTarget: { nodeId: 'heritage-city-hundred-industries', distance: 10 },
        duration: 6800,
        transition: 'settle',
        presenterNotes: 'Zakończ trzema typami dziedzictwa: zachowane, przekształcone, pamiętane.',
      },
    ],
  },
  {
    id: 'private-to-zpw',
    title: 'Od nazwisk do ZPW',
    eyebrow: 'ŚCIEŻKA PRZEMIANY / 04',
    description: 'Po 1945 roku prywatne fabryki nie znikają z mapy. Ich adresy zostają włączone do nowej organizacji, numerów i nazw państwowego przemysłu.',
    estimatedMinutes: 7,
    epilogue: 'System połączył rozproszone adresy, lecz każdy z nich zachował własne warstwy. Atlas pozwala zobaczyć je rok po roku.',
    atlasYear: 1948,
    atlasSiteId: 'zpw-03-1-maja-8',
    connections: [{ storyId: 'city-woven-from-industry', reason: 'Dalej przez przestrzeń · całe miasto', bridgeNodeId: 'place-bielsko-biala' }],
    steps: [
      {
        id: 'zpw-01',
        title: 'Przed zmianą są nazwiska',
        lead: 'Büttner, Jankowski, Mehlo-Kramer i Breitbart oznaczają różne rodziny, adresy i historie własności.',
        nodeIds: ['company-buttner', 'company-jankowski', 'company-mehlo-kramer', 'company-breitbart'],
        relationIds: ['r-buttner-founder', 'r-jankowski-arch'],
        layoutMode: 'network',
        cameraTarget: { nodeId: 'company-buttner', distance: 9 },
        duration: 5900,
        transition: 'draw',
        presenterNotes: 'Zacznij od wielości. Każda nazwa opisuje osobny układ rodzinny i przestrzenny.',
      },
      {
        id: 'zpw-02',
        title: 'Dokument zmienia strukturę',
        lead: 'Powojenne przejęcie nie przesuwa budynków. Przepisuje relacje własności i zarządzania.',
        nodeIds: ['doc-nationalization-1945', 'event-state-takeover-1945', 'company-buttner', 'company-mehlo-kramer', 'company-breitbart'],
        relationIds: ['r-state-document', 'r-state-mehlo', 'r-state-breitbart', 'r-buttner-successions'],
        timeRange: { start: '1945', end: '1951' },
        layoutMode: 'timeline',
        cameraTarget: { nodeId: 'event-state-takeover-1945', distance: 8 },
        duration: 6500,
        transition: 'settle',
        presenterNotes: 'Pokaż reorganizację jako zmianę połączeń, nie wymazanie wcześniejszej historii.',
      },
      {
        id: 'zpw-03',
        title: 'Rok 1948: jedno oznaczenie',
        lead: 'Rozproszone zakłady zostają wpisane w strukturę PZPW nr 14. Dawne adresy zaczynają działać jak elementy jednego układu.',
        nodeIds: ['event-state-takeover-1945', 'company-bewelana', 'company-mehlo-kramer', 'company-jankowski', 'place-bielsko-biala'],
        relationIds: ['r-state-buttner', 'r-state-mehlo', 'r-state-document'],
        timeRange: { start: '1948', end: '1949' },
        layoutMode: 'map',
        cameraTarget: { nodeId: 'place-bielsko-biala', distance: 11 },
        duration: 7200,
        transition: 'draw',
        presenterNotes: 'To pierwszy kadr nowej geografii organizacyjnej. Pozwól mapie pokazać rozproszenie adresów.',
      },
      {
        id: 'zpw-04',
        title: 'Nowe nazwy, stare hale',
        lead: 'Bewelana i BEFAMA kontynuują produkcję w miejscach ukształtowanych przez wcześniejsze przedsiębiorstwa.',
        nodeIds: ['company-buttner', 'company-bewelana', 'arch-buttner-hall', 'company-josephy', 'company-befama', 'arch-befama-complex'],
        relationIds: ['r-buttner-bewelana', 'r-bewelana-hall', 'r-josephy-befama', 'r-befama-complex'],
        timeRange: { start: '1950', end: '1965' },
        layoutMode: 'network',
        cameraTarget: { nodeId: 'company-bewelana', distance: 8 },
        duration: 6700,
        transition: 'ease',
        presenterNotes: 'Zachowaj jednocześnie dawną i nową nazwę — sednem jest ciągłość materialna przy zmianie systemu.',
      },
      {
        id: 'zpw-05',
        title: 'Zakład staje się rodziną adresów',
        lead: 'Jedna nazwa przedsiębiorstwa obejmuje kilka dawnych fabryk. Litery i numery porządkują nową skalę produkcji.',
        nodeIds: ['company-bewelana', 'arch-buttner-hall', 'arch-graubner-spinning', 'place-zywieckie-przedmiescie', 'place-bielsko-biala'],
        relationIds: ['r-bewelana-hall', 'r-bewelana-place', 'r-buttner-arch-place'],
        timeRange: { start: '1965', end: '1976' },
        layoutMode: 'map',
        cameraTarget: { nodeId: 'company-bewelana', distance: 10 },
        duration: 7200,
        transition: 'draw',
        presenterNotes: 'Połącz ten kadr z Atlasem: jedna organizacja jest rozpięta ponad wieloma fizycznymi miejscami.',
      },
      {
        id: 'zpw-06',
        title: 'Po systemie zostaje palimpsest',
        lead: 'Muzeum, zachowane fasady, nowe funkcje i nieustalone adresy pokazują różne dalsze losy tej samej przemysłowej sieci.',
        nodeIds: ['arch-old-factory-museum', 'heritage-museum-technology-1979', 'heritage-city-hundred-industries', 'photo-wega', 'person-piotr-kenig'],
        relationIds: ['r-old-factory-heritage', 'r-wega-heritage', 'r-piotr-sources', 'r-photo-wega-heritage'],
        timeRange: { start: '1979', end: 'now' },
        layoutMode: 'network',
        cameraTarget: { nodeId: 'heritage-city-hundred-industries', distance: 10 },
        duration: 7200,
        transition: 'settle',
        presenterNotes: 'Zakończ nie nostalgią, lecz zaproszeniem do czytania kolejnych warstw Atlasu.',
      },
    ],
  },
];

export const narrativeById = new Map(narratives.map((story) => [story.id, story]));
